<template>
  <div
    v-auto-animate="autoAnimateFade"
    :class="cn('flex h-full w-full flex-col items-center justify-center', props.class)"
  >
    <UiDialogHeader class="w-full">
      <UiDialogTitle>{{ t("dialog.title") }}</UiDialogTitle>
      <UiDialogDescription class="text-sm text-black/50">{{ t("dialog.description") }}</UiDialogDescription>
    </UiDialogHeader>

    <div class="flex flex-col gap-8 pb-20 pt-14" v-if="isError">
      <LucideFrown class="mx-auto size-20 text-black/50" />

      <PensionPortfolioRecommendationsHeader
        class="justify-center text-center"
        title="לא הצלחנו להציע המלצות"
        description="רוצה לנסות שוב?"
      />

      <UiButton @click="startRecommendationsFetching">נסה שוב</UiButton>
    </div>

    <div class="flex flex-col gap-8" v-else>
      <UiBubbleLoader :progress="progress">
        <p class="text-primary/75 font-medium">{{ t("loading") }}</p>
      </UiBubbleLoader>
    </div>

    <UiDialogFooter class="w-full md:pb-4" v-if="!isError">
      <UiButton
        class="min-md:ms-auto md:bg-primary md:h-10 md:w-full md:text-white"
        variant="outline"
        @click="startRecommendationsFetching"
      >
        <span v-text="isMobile ? $t('common.close') : $t('common.cancel')" />
      </UiButton>
    </UiDialogFooter>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query"
import { LucideFrown } from "lucide-vue-next"

const DEFAULT_PROGRESS = 20
const autoAnimateFade = { duration: 300 }

const props = defineProps<{
  class?: ClassValue
  onDone?: () => void
}>()

const retryAmount = ref(0)
const { t } = useI18n({ useScope: "local" })
const { t: $t } = useI18n({ useScope: "global" })
const { openSomethingWrongPopup } = useSomethingWrongPopup()

const { isMobile } = useDisplay()

const {
  isLoading,
  data: recommendationsData,
  refetch: refetchRecommendations,
} = useMyPensionRecommendations(undefined)

const { mutateAsync: postRecommendations, isError } = useMutation({
  mutationFn: PensionRecommendationsService.postRecommendations,
})

// Initialize the progress animation with our options
const { progress, startProgressAnimation, completeProgress } = useProgressAnimation({
  initialProgress: DEFAULT_PROGRESS,
  completionDelay: 1000,
  onComplete: () => props.onDone?.(),
})

async function startRecommendationsFetching() {
  if (retryAmount.value > 2) {
    openSomethingWrongPopup()
    return
  } else {
    retryAmount.value++
    startProgressAnimation()

    const result = await postRecommendations({})
    completeProgress()
    if (result.statusText === "error") {
      openSomethingWrongPopup()
    } else {
      await refetchRecommendations()
    }
  }
}

watchEffect(async () => {
  if (recommendationsData.value?.length) {
    completeProgress()
    console.log(recommendationsData.value)
  } else if (!isLoading.value && retryAmount.value === 0) {
    await startRecommendationsFetching()
  }
})
</script>

<i18n lang="json">
{
  "he": {
    "dialog": {
      "title": "ה-AI שלנו מחשב את התובנות הפנסיוניות שלך",
      "description": "בהתאם למידע המעודכן ה-AI עורך התאמות של התובנות עבורך"
    },
    "loading": "מעדכנים את הפרטים...",
    "error": "לא הצלחנו להציע המלצות",
    "tryAgain": "נסה שוב"
  }
}
</i18n>
